import type { JobAreaStats, JobGroupStats } from "./jobAreaStats";
import { UNSET_GROUP_KEY } from "./jobAreaStats";

/** What a group-share bar measures. */
export type JobStatsMeasure = "acres" | "samples" | "fields";

const MEASURES: Record<JobStatsMeasure, (stats: JobAreaStats) => number> = {
	acres: (s) => s.totalBoundaryAcres,
	samples: (s) => s.totalSamples,
	fields: (s) => s.fieldCount,
};

/** The slot of the groups folded into one "Other" segment. */
export const OTHER_GROUP_SLOT = -1;

/** One segment of a group-share bar: the group's color slot (or OTHER_GROUP_SLOT) and its share
 *  of the whole, 0–1. */
export interface JobGroupSegment {
	key: string;
	slot: number;
	share: number;
}

/** Groups largest first by boundary acres, the unset bucket kept last. The order is the same
 *  whatever the bar measures, so a group keeps its row and its color when the measure changes. */
export function rankJobGroups(groups: readonly JobGroupStats[]): JobGroupStats[] {
	const set = groups.filter((g) => g.key !== UNSET_GROUP_KEY);
	const unset = groups.filter((g) => g.key === UNSET_GROUP_KEY);
	return [...set.sort((a, b) => b.stats.totalBoundaryAcres - a.stats.totalBoundaryAcres), ...unset];
}

/**
 * Each group's color slot, in rank order: every group gets its own when there are no more groups
 * than slots; otherwise the first `slots - 1` do and the rest share OTHER_GROUP_SLOT.
 */
export function jobGroupSlots(ranked: readonly JobGroupStats[], slots: number): Map<string, number> {
	const colored = ranked.length <= slots ? ranked.length : slots - 1;
	return new Map(ranked.map((g, i) => [g.key, i < colored ? i : OTHER_GROUP_SLOT]));
}

/**
 * The bar's segments for a measure, in rank order with "Other" last. A group with nothing of the
 * measure has no segment; the bar is empty when no group has any.
 */
export function jobGroupSegments(
	ranked: readonly JobGroupStats[],
	slotByKey: ReadonlyMap<string, number>,
	measure: JobStatsMeasure,
): JobGroupSegment[] {
	const valueOf = MEASURES[measure];
	const total = ranked.reduce((sum, g) => sum + valueOf(g.stats), 0);
	if (total <= 0) return [];
	const segments: JobGroupSegment[] = [];
	let other = 0;
	for (const group of ranked) {
		const value = valueOf(group.stats);
		if (value <= 0) continue;
		const slot = slotByKey.get(group.key) ?? OTHER_GROUP_SLOT;
		if (slot === OTHER_GROUP_SLOT) other += value;
		else segments.push({ key: group.key, slot, share: value / total });
	}
	if (other > 0) segments.push({ key: "Other", slot: OTHER_GROUP_SLOT, share: other / total });
	return segments;
}
